import { Box, Chip, IconButton, Popover, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useState } from "react";
import { AlertMetrics } from "../types";
import Panel from "../components/Panel";
import StatTile from "../components/StatTile";
import { relativeTime } from "../components/format";

export default function AlertPanel({ m }: { m: AlertMetrics }) {
    const [errPopover, setErrPopover] = useState<{ anchor: HTMLElement; message: string } | null>(null);

    return (
        <Panel title="Alert manager">
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <StatTile label="Crons running" value={m.cronsRunning} hint="enabled alerts" />
                <StatTile label="Alerts triggered" value={(m.alertsTriggered ?? 0).toLocaleString()} />
                <StatTile label="Last cron run" value={relativeTime(m.lastCronRunAt)} />
            </Stack>
            <Typography variant="subtitle2" gutterBottom>Notification providers</Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Working</TableCell>
                        <TableCell align="right">Sent</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell>Last OK</TableCell>
                        <TableCell>Last error</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(m.providers || []).map((p) => (
                        <TableRow key={p.name}>
                            <TableCell sx={{ textTransform: "capitalize" }}>{p.name}</TableCell>
                            <TableCell>{p.enabled ? "Yes" : "No"}</TableCell>
                            <TableCell>
                                {!p.enabled
                                    ? <Chip size="small" variant="outlined" label="—" />
                                    : <Chip size="small" variant="outlined" color={p.working ? "success" : "error"} label={p.working ? "OK" : "Failing"} />}
                            </TableCell>
                            <TableCell align="right">{p.sent}</TableCell>
                            <TableCell align="right">{p.failed}</TableCell>
                            <TableCell>{relativeTime(p.lastOkAt)}</TableCell>
                            <TableCell>
                                {p.lastError ? (
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                        <Typography variant="caption" color={p.working ? "text.secondary" : "error.main"}>
                                            {relativeTime(p.lastErrorAt)}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            aria-label="view error message"
                                            onClick={(e) => setErrPopover({ anchor: e.currentTarget, message: p.lastError! })}
                                        >
                                            <ErrorOutlineIcon fontSize="small" color={p.working ? "disabled" : "error"} />
                                        </IconButton>
                                    </Stack>
                                ) : "—"}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Popover
                open={!!errPopover}
                anchorEl={errPopover?.anchor}
                onClose={() => setErrPopover(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ p: 1.5, maxWidth: 440 }}>
                    <Typography variant="caption" color="text.secondary">Last error</Typography>
                    <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace" }}
                    >
                        {errPopover?.message}
                    </Typography>
                </Box>
            </Popover>
        </Panel>
    );
}
